import React, { useState } from "react";
import * as t from "io-ts";
import { CRMButtonQuaternary } from "../../CRMButtonQuaternary/CRMButtonQuaternary";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { postJson } from "../../../wrappers/restRequest";
import * as FirstPartyFetchResponse from "../../../../../domain/models/FirstPartyFetchResponse";

const GenerateStudyResponseCodec = t.type({
    result: t.string,
    reason: t.union([t.string, t.null]),
});

type TStatus = "idle" | "running" | "started" | "posted" | "skipped" | "failed" | "error";

interface ICRMCaseGenerateStudyButtonProps {
    caseId: string;
}

const statusMessage = (status: TStatus, reason: string | null): string | null => {
    switch (status) {
        case "started":
            return "Generating… the draft will appear in #case-studies on Slack shortly.";
        case "posted":
            return "Draft posted to #case-studies on Slack.";
        case "skipped":
            return "Nothing posted — this case isn't eligible (not completed, abandoned, or a dual-rep sale leg).";
        case "failed":
            return `Couldn't generate the case study${reason ? ` (${reason})` : ""}. If this is SLACK_POST_MESSAGE_FAILED, the bot needs inviting to #case-studies.`;
        case "error":
            return "Couldn't reach the server. Try again shortly.";
        default:
            return null;
    }
};

export const CRMCaseGenerateStudyButton = (props: ICRMCaseGenerateStudyButtonProps): JSX.Element => {
    const [status, setStatus] = useState<TStatus>("idle");
    const [reason, setReason] = useState<string | null>(null);

    const handleClick = (): void => {
        if (status === "running") {
            return;
        }
        setStatus("running");
        setReason(null);
        postJson({
            url: `/v1/cases/${props.caseId}/generate-case-study`,
            body: {},
            dataCodec: GenerateStudyResponseCodec,
            defaultData: { result: "", reason: null },
        })().then((response) => {
            if (response.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                const data = response.response.data;
                if (data.result === "started") {
                    setStatus("started");
                } else if (data.result === "posted") {
                    setStatus("posted");
                } else if (data.result === "failed") {
                    setReason(data.reason);
                    setStatus("failed");
                } else {
                    setStatus("skipped");
                }
            } else {
                setStatus("error");
            }
        });
    };

    const message = statusMessage(status, reason);

    return (
        <SpacingColumn spacing={CRMSpacing.TINY}>
            <CRMButtonQuaternary
                label={status === "running" ? "Generating case study…" : "Generate case study"}
                icon="robot"
                highlight="positive-green"
                disabled={status === "running"}
                onClick={handleClick}
            />
            {message !== null && <CRMParagraph>{message}</CRMParagraph>}
        </SpacingColumn>
    );
};
